import { request } from "@umijs/max";
import { getAccessToken } from "@/services/auth/tokenManager";

const BASE = process.env.Auth_URL || "";

export type ProjectStatus =
  | "draft"
  | "parsing"
  | "parsed"
  | "extracting_chars"
  | "characters_ready"
  | "extracting_scenes"
  | "scenes_ready"
  | "failed";

export interface ProjectMeta {
  era?: string;
  genre?: string[];
  episodes?: number;
  location?: string;
  synopsis?: string;
}

export type ExtractStatus = "processing" | "ready" | "failed" | "";

export interface ParseDiagnostics {
  episode_marker_regex?: string;
  used_fallback?: boolean;
  episode_count?: number;
  overall_coverage?: number;
  partial_episodes?: number[];
  failed_episodes?: number[];
  enrich_enabled?: boolean;
  enrich_failed?: number[];

  enrich_done?: boolean;
}

export interface Project {
  id: string;
  user_id: string;
  title: string;
  raw_text?: string;
  meta: ProjectMeta;
  episode_count: number;
  scene_count: number;
  character_count: number;
  scene_ref_count?: number;
  status: ProjectStatus;
  parse_progress?: number;
  error?: string;
  parse_diagnostics?: ParseDiagnostics;

  cover_image_url?: string;

  extract_chars_status?: ExtractStatus;
  extract_chars_error?: string;
  extract_chars_started_at?: string;
  extract_scenes_status?: ExtractStatus;
  extract_scenes_error?: string;
  extract_scenes_started_at?: string;

  episode_status_counts?: Record<string, number>;

  project_role?: ProjectMemberRole;
  created_at: string;
  updated_at: string;
}

interface Envelope<T> {
  success: boolean;
  data?: T;

  message?: string;

  error?: { code: string; message?: string; details?: Record<string, any> };
}

export async function listProjects(params: { page?: number; size?: number }) {
  return request<
    Envelope<{ items: Project[]; total: number; page: number; size: number }>
  >(`${BASE}/api/workspace/projects`, {
    method: "GET",
    params,
    skipErrorHandler: true,
  });
}

export async function createProject(data: {
  title: string;
  raw_text?: string;
}) {
  return request<Envelope<Project>>(`${BASE}/api/workspace/projects`, {
    method: "POST",
    data,
    skipErrorHandler: true,
  });
}

export interface ProjectPatch {
  title?: string;
  raw_text?: string;
  synopsis?: string;
  era?: string;
  genre?: string[];
  location?: string;
  episodes?: number;
}

export async function updateProject(id: string, data: ProjectPatch) {
  return request<Envelope<Project>>(`${BASE}/api/workspace/projects/${id}`, {
    method: "PATCH",
    data,
    skipErrorHandler: true,
  });
}

export async function resetProject(id: string) {
  return request<Envelope<Project>>(
    `${BASE}/api/workspace/projects/${id}/reset`,
    {
      method: "POST",
      skipErrorHandler: true,
      timeout: 60_000,
    },
  );
}

export async function getProject(id: string, withRaw = false) {
  return request<Envelope<Project>>(`${BASE}/api/workspace/projects/${id}`, {
    method: "GET",
    params: withRaw ? { raw: 1 } : {},
    skipErrorHandler: true,
  });
}

export async function deleteProject(id: string) {
  return request<Envelope<{ deleted: boolean }>>(
    `${BASE}/api/workspace/projects/${id}`,
    {
      method: "DELETE",
      skipErrorHandler: true,
    },
  );
}

export async function parseProject(id: string, forceEps?: number[]) {
  const qs =
    forceEps && forceEps.length > 0 ? `?force_eps=${forceEps.join(",")}` : "";
  return request<Envelope<Project>>(
    `${BASE}/api/workspace/projects/${id}/parse${qs}`,
    {
      method: "POST",
      skipErrorHandler: true,
      timeout: 200_000,
    },
  );
}

export async function extractCharacters(id: string) {
  return request<Envelope<Project>>(
    `${BASE}/api/workspace/projects/${id}/extract-characters`,
    { method: "POST", skipErrorHandler: true },
  );
}

export async function extractScenes(projectId: string) {
  return request<Envelope<Project>>(
    `${BASE}/api/workspace/projects/${projectId}/extract-scenes`,
    { method: "POST", skipErrorHandler: true },
  );
}

// 后端聚合多 collection,按 created_at 倒序;第一页 Redis 缓存 30s。
export type DramaOutputSource =
  | "character_shot"
  | "character_sheet"
  | "storyboard_frame"
  | "canvas_image"
  | "canvas_video"
  | "canvas_audio"
  | "storyboard_audio";

export type DramaOutputMediaType = "image" | "video" | "audio";

export interface DramaOutputItem {
  source: DramaOutputSource;
  source_id: string;
  sub_id?: string;
  project_id?: string;
  project_title?: string;
  character_id?: string;
  storyboard_id?: string;
  ep_no?: number;
  scene_no?: string;
  shot_no?: number;
  slot?: "first" | "middle" | "last";
  kind?: string;
  media_type?: DramaOutputMediaType;
  // 历史字段名,实际是通用 URL,canvas_video/audio 也填这里。
  image_url: string;
  prompt?: string;
  bytes?: number;
  status: string;
  created_at: string;
  completed_at?: string;
}

export interface DramaOutputsListResp {
  total: number;
  page: number;
  size: number;
  items: DramaOutputItem[];
  cached?: boolean;
}

export async function listDramaOutputs(params: {
  page?: number;
  size?: number;
  source?: DramaOutputSource | "";
  project_id?: string;
}) {
  const qp: Record<string, string | number> = {
    page: params.page ?? 1,
    size: params.size ?? 40,
  };
  if (params.source) qp.source = params.source;
  if (params.project_id) qp.project_id = params.project_id;
  return request<Envelope<DramaOutputsListResp>>(
    `${BASE}/api/workspace/outputs`,
    { method: "GET", params: qp, skipErrorHandler: true, timeout: 15_000 },
  );
}

// 唯一键 (user_id, source_url) 复用;MinIO 未配 → 503 E_MINIO_DISABLED。
export type ExternalImportStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export interface ExternalImport {
  id: string;
  user_id: string;
  source_type: string;
  source_path?: string;
  source_url: string;
  name: string;
  category: string;
  folder?: string;
  notes?: string;
  tags?: string[];
  bytes?: number;
  content_type?: string;
  minio_bucket?: string;
  minio_object_key?: string;
  minio_url?: string;
  status: ExternalImportStatus;
  error?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface ExternalImportListResp {
  items: ExternalImport[];
  total: number;
  page: number;
  size: number;
}

export interface CreateImportPayload {
  source_type?: string;
  source_url: string;
  source_path?: string;
  name?: string;
  category?: string;
  folder?: string;
  notes?: string;
  tags?: string[];
}

export interface BatchImportItem {
  source_url: string;
  source_path?: string;
  name?: string;
  category?: string;
}

export interface BatchImportPayload {
  source_type?: string;
  items: BatchImportItem[];
  folder?: string;
  notes?: string;
  tags?: string[];
}

export interface BatchImportResp {
  items: ExternalImport[];
  submitted: number;
}

export interface PatchImportPayload {
  folder?: string;
  notes?: string;
  tags?: string[];
}

export async function listImports(params: {
  page?: number;
  size?: number;
  folder?: string;
  category?: string;
  keyword?: string;
  status?: ExternalImportStatus | "";
}) {
  const qp: Record<string, string | number> = {
    page: params.page ?? 1,
    size: params.size ?? 40,
  };
  if (params.folder) qp.folder = params.folder;
  if (params.category) qp.category = params.category;
  if (params.keyword) qp.keyword = params.keyword;
  if (params.status) qp.status = params.status;
  return request<Envelope<ExternalImportListResp>>(
    `${BASE}/api/workspace/imports`,
    { method: "GET", params: qp, skipErrorHandler: true, timeout: 15_000 },
  );
}

export async function listImportFolders() {
  return request<Envelope<{ folders: string[] }>>(
    `${BASE}/api/workspace/imports/folders`,
    { method: "GET", skipErrorHandler: true, timeout: 5_000 },
  );
}

export async function createImport(body: CreateImportPayload) {
  return request<Envelope<ExternalImport>>(`${BASE}/api/workspace/imports`, {
    method: "POST",
    data: body,
    skipErrorHandler: true,
    timeout: 10_000,
  });
}

export async function batchImport(body: BatchImportPayload) {
  return request<Envelope<BatchImportResp>>(
    `${BASE}/api/workspace/imports/batch`,
    { method: "POST", data: body, skipErrorHandler: true, timeout: 30_000 },
  );
}

export async function patchImport(id: string, body: PatchImportPayload) {
  return request<Envelope<ExternalImport>>(
    `${BASE}/api/workspace/imports/${id}`,
    { method: "PATCH", data: body, skipErrorHandler: true, timeout: 5_000 },
  );
}

export async function deleteImport(id: string) {
  return request<Envelope<{ deleted: boolean }>>(
    `${BASE}/api/workspace/imports/${id}`,
    { method: "DELETE", skipErrorHandler: true, timeout: 5_000 },
  );
}

// 批量查重,单次最多 200 条。
export interface CheckImportEntry {
  imported: boolean;
  id?: string;
  status?: ExternalImportStatus;
  folder?: string;
}

export async function checkImports(sourceURLs: string[]) {
  return request<Envelope<{ entries: Record<string, CheckImportEntry> }>>(
    `${BASE}/api/workspace/imports/check`,
    {
      method: "POST",
      data: { source_urls: sourceURLs },
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// multipart 直传,同步落库 completed;单文件上限 50MB。
export async function uploadImport(
  file: File | Blob,
  opts?: {
    sourceType?: string;
    name?: string;
    folder?: string;
    notes?: string;
    tags?: string[];
  },
) {
  const token = getAccessToken();
  const fd = new FormData();
  const filename =
    opts?.name ||
    (file as File).name ||
    `paste-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
  fd.append("file", file, filename);
  if (opts?.sourceType) fd.append("source_type", opts.sourceType);
  if (opts?.name) fd.append("name", opts.name);
  if (opts?.folder) fd.append("folder", opts.folder);
  if (opts?.notes) fd.append("notes", opts.notes);
  if (opts?.tags && opts.tags.length > 0)
    fd.append("tags", opts.tags.join(","));
  const resp = await fetch(`${BASE}/api/workspace/imports/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  const json = (await resp.json()) as Envelope<ExternalImport>;
  return json;
}

// max=0 表示后端关闭限流(LLM_MAX_CONCURRENT=0)。
export interface LLMInFlightTask {
  holder: string;
  caller: string;
  user_id: string;
  user_name?: string;
  project_id: string;
  project_title?: string;
  note: string;
  started_at: string;
  elapsed_ms: number;
  expires_at: string;
}

export interface LLMWaitingTask {
  holder: string;
  caller: string;
  user_id: string;
  user_name?: string;
  project_id: string;
  project_title?: string;
  note: string;
  enqueued_at: string;
  waited_ms: number;
}

export interface LLMInFlightResult {
  tasks: LLMInFlightTask[];
  waiting: LLMWaitingTask[];
  count: number;
  waiting_count: number;
  max: number;
  lease_seconds: number;
  degraded?: boolean;
  error?: string;
}

export async function getLLMInFlight() {
  return request<Envelope<LLMInFlightResult>>(
    `${BASE}/api/workspace/llm/in-flight`,
    { method: "GET", skipErrorHandler: true, timeout: 5_000 },
  );
}

// provider 未配 maxConcurrent / maxRpm 时不会注册到 registry,因此本接口返回的列表 = 真正
// 受限流管控的 provider 集合;前端按列表渲染卡片(空数组 = 全部 provider 未配限流)。
export interface ProviderInFlightItem {
  provider: string;
  max_concurrent: number;
  max_rpm: number;
  current: number;
  current_rpm: number;
  lease_seconds: number;
  tasks: LLMInFlightTask[];
  waiting: LLMWaitingTask[];
  waiting_count: number;
}

export interface ProvidersInFlightResult {
  providers: ProviderInFlightItem[];
}

export async function getProvidersInFlight() {
  return request<Envelope<ProvidersInFlightResult>>(
    `${BASE}/api/workspace/admin/providers/in-flight`,
    { method: "GET", skipErrorHandler: true, timeout: 5_000 },
  );
}

// 与 drama/internal/models/canvas.go 对齐;data 是开放 map。
export interface CanvasViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface CanvasPosition {
  x: number;
  y: number;
}

export interface CanvasNode<D = Record<string, unknown>> {
  id: string;
  type: string;
  position: CanvasPosition;
  data?: D;
  width?: number;
  height?: number;
}

export interface CanvasEdge {
  id: string;
  source: string;
  target: string;
  source_handle?: string;
  target_handle?: string;
  label?: string;
}

export interface Canvas {
  id?: string;
  project_id: string;
  user_id: string;
  viewport: CanvasViewport;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  created_at?: string;
  updated_at?: string;
}

export async function getCanvas(projectId: string) {
  return request<Envelope<Canvas>>(
    `${BASE}/api/workspace/projects/${projectId}/canvas`,
    { method: "GET", skipErrorHandler: true },
  );
}

// 用户产品意图任务维度,跨 kind 聚合多 collection。
export type BusinessTaskKind =
  | 'parse-project' | 'adapt-novel' | 'extract-characters' | 'extract-scenes' | 'gen-storyboard'
  | 'canvas-image' | 'canvas-video' | 'canvas-audio' | 'reverse-prompt-image' | 'reverse-prompt-video';

export type BusinessTaskStatus =
  | "queued"
  | "processing"
  | "completed"
  | "partial"
  | "failed"
  | "canceled";

export interface BusinessTaskProgress {
  done: number;
  total: number;
  pct: number;
}

export interface BusinessTask {
  wait_timed_out?: boolean;
  id: string;
  kind: BusinessTaskKind;
  status: BusinessTaskStatus;
  user_id?: string;
  project_id?: string;
  project_title?: string;
  target_id?: string;
  target_label?: string;
  /** 前端按 `monitor.tasks.subkind.<key>` i18n 优先于 kind label。 */
  sub_kind?: string;
  progress?: BusinessTaskProgress;
  request_count: number;
  /** BYOK / 老数据 / LLM 路径未持久化为 0。 */
  credits_cost?: number;
  provider_name?: string;
  model_id?: string;
  started_at?: string;
  completed_at?: string;
  elapsed_ms: number;
  error?: string;
  link?: string;
}

export interface BusinessTaskRequest {
  id: string;
  kind: "apimart" | "llm" | string;
  label: string;
  status: string;
  progress?: number;
  created_at?: string;
  completed_at?: string;
  elapsed_ms?: number;
  error?: string;
  image_url?: string;
  credits_cost?: number;
  provider_name?: string;
  model_id?: string;
}

export interface BusinessTaskListCounts {
  queued?: number;
  processing: number;
  completed: number;
  partial: number;
  failed: number;
  total: number;
}

export interface BusinessTaskListResult {
  tasks: BusinessTask[];
  counts: BusinessTaskListCounts;
  pagination?: {
    page: number;
    page_size: number;
    has_more: boolean;
    total?: number;
    total_capped?: boolean;
  };
  degraded?: boolean;
  error?: string;
}

export interface BusinessTaskDetailResult {
  task: BusinessTask;
  requests: BusinessTaskRequest[];
}

export async function listMyTasks(opts?: {
  status?: BusinessTaskStatus;
  kind?: BusinessTaskKind;
  limit?: number;
  // 终态时间窗口(小时):undefined → 默认 24;0 → 不限。
  windowHours?: number;
}) {
  const params: Record<string, string | number> = { queue_status: 1 };
  if (opts?.status) params.status = opts.status;
  if (opts?.kind) params.kind = opts.kind;
  if (opts?.limit) params.limit = opts.limit;
  if (opts?.windowHours !== undefined) params.window_hours = opts.windowHours;
  return request<Envelope<BusinessTaskListResult>>(
    `${BASE}/api/workspace/me/tasks`,
    { method: "GET", params, skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function getMyTaskDetail(
  kind: BusinessTaskKind,
  targetId: string,
) {
  return request<Envelope<BusinessTaskDetailResult>>(
    `${BASE}/api/workspace/me/tasks/${kind}/${encodeURIComponent(targetId)}`,
    { method: "GET", params: { queue_status: 1 }, skipErrorHandler: true, timeout: 8_000 },
  );
}

// 复合任务取消该实体下所有 in-flight 子任务。
export async function cancelMyTask(kind: BusinessTaskKind, targetId: string) {
  return request<Envelope<{ affected: number }>>(
    `${BASE}/api/workspace/me/tasks/${kind}/${encodeURIComponent(targetId)}/cancel`,
    { method: "POST", skipErrorHandler: true, timeout: 8_000 },
  );
}

// userId 空 = 跨所有用户;非空 = 只列该用户。
export async function listAdminTasks(opts?: {
  userId?: string;
  taskId?: string;
  status?: BusinessTaskStatus;
  kind?: BusinessTaskKind;
  page?: number;
  pageSize?: number;
  limit?: number;
  windowHours?: number;
}) {
  const params: Record<string, string | number> = { queue_status: 1 };
  if (opts?.userId) params.user_id = opts.userId;
  if (opts?.taskId) params.task_id = opts.taskId;
  if (opts?.status) params.status = opts.status;
  if (opts?.kind) params.kind = opts.kind;
  if (opts?.page) params.page = opts.page;
  if (opts?.pageSize) params.page_size = opts.pageSize;
  if (opts?.limit) params.limit = opts.limit;
  if (opts?.windowHours !== undefined) params.window_hours = opts.windowHours;
  return request<Envelope<BusinessTaskListResult>>(
    `${BASE}/api/workspace/admin/tasks`,
    { method: "GET", params, skipErrorHandler: true, timeout: 10_000 },
  );
}

export interface CostRecord {
  id: string;
  job_id: string;
  user_id?: string;
  kind: string;
  model_id?: string;
  provider?: string;
  status: string;
  basis: string; // token | per_second | per_request
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  duration_sec?: number;
  resolution?: string;
  aspect_ratio?: string;
  credit_cost: number;
  created_at: string;
}

export async function listAdminCostRecords(opts?: {
  userId?: string;
  modelId?: string;
  kind?: string;
  basis?: string;
  page?: number;
  size?: number;
}) {
  const params: Record<string, string | number> = {};
  if (opts?.userId) params.user_id = opts.userId;
  if (opts?.modelId) params.model_id = opts.modelId;
  if (opts?.kind) params.kind = opts.kind;
  if (opts?.basis) params.basis = opts.basis;
  if (opts?.page) params.page = opts.page;
  if (opts?.size) params.size = opts.size;
  return request<
    Envelope<{ items: CostRecord[]; total: number; page: number }>
  >(`${BASE}/api/workspace/admin/cost-records`, {
    method: "GET",
    params,
    skipErrorHandler: true,
    timeout: 10_000,
  });
}

export async function getAdminTaskDetail(
  kind: BusinessTaskKind,
  targetId: string,
  userId?: string,
) {
  const params: Record<string, string> = { queue_status: "1" };
  if (userId) params.user_id = userId;
  return request<Envelope<BusinessTaskDetailResult>>(
    `${BASE}/api/workspace/admin/tasks/${kind}/${encodeURIComponent(targetId)}`,
    { method: "GET", params, skipErrorHandler: true, timeout: 10_000 },
  );
}

// 标记式取消(同 cancelMyTask)。
export async function cancelAdminTask(
  kind: BusinessTaskKind,
  targetId: string,
  userId?: string,
) {
  const params: Record<string, string> = {};
  if (userId) params.user_id = userId;
  return request<Envelope<{ affected: number }>>(
    `${BASE}/api/workspace/admin/tasks/${kind}/${encodeURIComponent(targetId)}/cancel`,
    { method: "POST", params, skipErrorHandler: true, timeout: 8_000 },
  );
}

// ===== 调用链 trace(admin 调试用)=====

export interface TaskTraceEvent {
  id: string;
  task_id: string;
  job_id?: string;
  user_id?: string;
  phase: string;
  attempt?: number;
  provider?: string;
  model?: string;
  method?: string;
  url?: string;
  status_code?: number;
  latency_ms?: number;
  request_headers?: Record<string, string>;
  request_body?: string;
  response_headers?: Record<string, string>;
  response_body?: string;
  error?: string;
  note?: string;
  created_at: string;
}

export async function getAdminTaskTrace(
  kind: BusinessTaskKind,
  targetId: string,
  opts?: { phase?: string; onlyError?: boolean; limit?: number },
) {
  const params: Record<string, string | number> = {};
  if (opts?.phase) params.phase = opts.phase;
  if (opts?.onlyError) params.only_error = "true";
  if (opts?.limit) params.limit = opts.limit;
  return request<
    Envelope<{ events: TaskTraceEvent[]; total: number; task_id: string }>
  >(
    `${BASE}/api/workspace/admin/tasks/${kind}/${encodeURIComponent(targetId)}/trace`,
    { method: "GET", params, skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function deleteAdminTracesBefore(beforeISO: string) {
  return request<Envelope<{ deleted: number; before: string }>>(
    `${BASE}/api/workspace/admin/traces`,
    {
      method: "DELETE",
      params: { before: beforeISO },
      skipErrorHandler: true,
      timeout: 30_000,
    },
  );
}

export type ProjectMemberRole = "owner" | "editor" | "viewer";

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: ProjectMemberRole;
  invited_by?: string;
  joined_at: string;
}

export type ProjectInvitationStatus =
  | "pending"
  | "accepted"
  | "revoked"
  | "expired"
  | "replaced";

export interface ProjectInvitation {
  id: string;
  project_id: string;
  project_title: string;
  inviter_id: string;
  inviter_name: string;
  invitee_key: string;
  role: ProjectMemberRole;
  token: string;
  message?: string;
  status: ProjectInvitationStatus;
  expire_at: string;
  created_at: string;
  updated_at: string;
  accepted_by?: string;
  accepted_at?: string;
}

// path: added=已注册直加 / invited=未注册发邀请 / lookup_failed=降级邀请。
export interface InviteResult {
  path: "added" | "invited" | "lookup_failed";
  member_id?: string;
  invitation_id?: string;
  invitee_user_id?: string;
  mail_queued?: boolean;
}

export interface InvitationPreview {
  project_id: string;
  project_title: string;
  inviter_name: string;
  role: ProjectMemberRole;
  role_label: string;
  invitee_key: string;
  message?: string;
  expire_at: string;
}

export async function listProjectMembers(projectId: string) {
  return request<Envelope<{ items: ProjectMember[] }>>(
    `${BASE}/api/workspace/projects/${projectId}/members`,
    { method: "GET", skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function inviteProjectMember(
  projectId: string,
  body: { email: string; role: ProjectMemberRole; message?: string },
) {
  return request<Envelope<InviteResult>>(
    `${BASE}/api/workspace/projects/${projectId}/members`,
    { method: "POST", data: body, skipErrorHandler: true, timeout: 12_000 },
  );
}

export async function updateProjectMemberRole(
  projectId: string,
  uid: string,
  role: ProjectMemberRole,
) {
  return request<Envelope<{ updated: boolean }>>(
    `${BASE}/api/workspace/projects/${projectId}/members/${uid}`,
    { method: "PATCH", data: { role }, skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function removeProjectMember(projectId: string, uid: string) {
  return request<Envelope<{ removed: boolean }>>(
    `${BASE}/api/workspace/projects/${projectId}/members/${uid}`,
    { method: "DELETE", skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function transferProjectOwner(projectId: string, toUid: string) {
  return request<Envelope<{ new_owner: string }>>(
    `${BASE}/api/workspace/projects/${projectId}/transfer`,
    {
      method: "POST",
      data: { to_uid: toUid },
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function listProjectInvitations(
  projectId: string,
  status?: ProjectInvitationStatus,
) {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  return request<Envelope<{ items: ProjectInvitation[] }>>(
    `${BASE}/api/workspace/projects/${projectId}/invitations`,
    { method: "GET", params, skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function revokeProjectInvitation(
  projectId: string,
  invitationId: string,
) {
  return request<Envelope<{ revoked: boolean }>>(
    `${BASE}/api/workspace/projects/${projectId}/invitations/${invitationId}/revoke`,
    { method: "POST", skipErrorHandler: true, timeout: 8_000 },
  );
}

// token 字符集 A-Za-z0-9-_,直接拼 path,二次 encodeURIComponent 多余。
export async function getInvitationByToken(token: string) {
  return request<Envelope<InvitationPreview>>(
    `${BASE}/api/workspace/invitations/${token}`,
    { method: "GET", skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function acceptInvitation(token: string) {
  return request<
    Envelope<{
      project_id: string;
      project_title: string;
      role: ProjectMemberRole;
    }>
  >(`${BASE}/api/workspace/invitations/${token}/accept`, {
    method: "POST",
    skipErrorHandler: true,
    timeout: 10_000,
  });
}

// 然后写回目标 raw_text,并给派生表打 revert_stale_at。
export type ProjectRevisionTrigger =
  | "auto_idle"
  | "auto_threshold"
  | "exit"
  | "manual"
  | "revert";

export interface ProjectRevision {
  id: string;
  project_id: string;
  user_id: string;
  created_by: string;
  created_by_name?: string;
  version_no: number;
  // 仅 GET 单条 + 回滚响应里返回;列表不带。
  raw_text?: string;
  content_hash: string;
  trigger: ProjectRevisionTrigger;
  label?: string;
  char_count: number;
  chars_delta: number;
  base_version_no?: number;
  created_at: string;
}

export async function listProjectRevisions(
  projectId: string,
  params: { limit?: number; offset?: number } = {},
) {
  return request<
    Envelope<{
      items: ProjectRevision[];
      total: number;
      limit: number;
      offset: number;
    }>
  >(`${BASE}/api/workspace/projects/${projectId}/revisions`, {
    method: "GET",
    params,
    skipErrorHandler: true,
    timeout: 8_000,
  });
}

export async function getProjectRevision(projectId: string, versionNo: number) {
  return request<Envelope<ProjectRevision>>(
    `${BASE}/api/workspace/projects/${projectId}/revisions/${versionNo}`,
    { method: "GET", skipErrorHandler: true, timeout: 8_000 },
  );
}

// raw_text 缺省时后端取 db 最新值;主路径请显式传值避免与 updateProject 形成 race。
export async function createProjectRevision(
  projectId: string,
  body: {
    raw_text?: string;
    trigger?: ProjectRevisionTrigger;
    label?: string;
  } = {},
) {
  return request<Envelope<ProjectRevision>>(
    `${BASE}/api/workspace/projects/${projectId}/revisions`,
    { method: "POST", data: body, skipErrorHandler: true, timeout: 8_000 },
  );
}

export async function revertProjectRevision(
  projectId: string,
  versionNo: number,
) {
  return request<Envelope<{ revision: ProjectRevision; project?: Project }>>(
    `${BASE}/api/workspace/projects/${projectId}/revisions/${versionNo}/revert`,
    { method: "POST", skipErrorHandler: true, timeout: 15_000 },
  );
}

/** 维度级 credit 价格规则(载体 = caller route 的 ModelPricing[])。Match AND 语义,
 *  数组顺序匹配,全不命中 → fallback `ModelPricing.creditPrice`。 */
export interface PriceRule {
  match: Record<string, string>;
  price: number;
  note?: string;
}

/** 备用供应商候选(有序)。主 = GenModel.providerName + genericConfig 为候选[0],本数组是候选[1..n]。 */
export interface ProviderCandidate {
  providerName: string;
  /** 该供应商上游真实 model 名,留空 = 用全局 modelId。 */
  upstreamModel?: string;
  /** 留空 = 继承主候选协议。 */
  genericConfig?: Record<string, unknown>;
  enabled: boolean;
}

/** model 元数据 + 启用 + GenericConfig;价格在 caller route 的 ModelPricing[]。 */
export interface GenModel {
  id?: string;
  modelId: string;
  providerName: string;
  displayName: string;
  category: "image" | "video" | "audio" | "llm";
  enabled: boolean;
  /** lobehub icon key,前端渲染品牌 logo。 */
  icon?: string;
  paramsSchema?: Record<string, any>;
  defaultExtra?: Record<string, any>;
  /** 配置化引擎参数,形态见 drama/internal/adapters/generic/engine.go Config。 */
  genericConfig?: Record<string, unknown>;
  /** 主供应商上游真实 model 名,留空 = 用 modelId。 */
  upstreamModel?: string;
  /** 备用供应商候选(有序)。主候选 submit 失败时按顺序切下一个。 */
  providerCandidates?: ProviderCandidate[];
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpsertGenModelRequest {
  modelId: string;
  providerName: string;
  displayName: string;
  category: "image" | "video" | "audio" | "llm";
  enabled?: boolean;
  icon?: string;
  paramsSchema?: Record<string, any>;
  defaultExtra?: Record<string, any>;
  genericConfig?: Record<string, unknown>;
  upstreamModel?: string;
  /** 非 undefined(含空数组)整体覆盖。 */
  providerCandidates?: ProviderCandidate[];
  sortOrder?: number;
}

export async function listEnabledModels(
  category?: "image" | "video" | "audio" | "llm",
) {
  return request<Envelope<{ models: GenModel[] }>>(
    `${BASE}/api/workspace/models`,
    {
      method: "GET",
      params: category ? { category } : undefined,
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function listAdminGenModels(
  params: {
    category?: "image" | "video" | "audio" | "llm";
    providerName?: string;
  } = {},
) {
  return request<Envelope<{ models: GenModel[] }>>(
    `${BASE}/api/workspace/admin/models`,
    {
      method: "GET",
      params,
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function getAdminGenModel(modelId: string) {
  return request<Envelope<GenModel>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function upsertAdminGenModel(
  modelId: string,
  body: UpsertGenModelRequest,
) {
  return request<Envelope<GenModel>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}`,
    {
      method: "PUT",
      data: body,
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function deleteAdminGenModel(modelId: string) {
  return request<Envelope<{ deleted: string }>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}`,
    {
      method: "DELETE",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// 后端非事务,Rename(drama_models)成功后才执行 RenameModelID(drama_caller_routes);
// 第二步失败返 500,模型已改名但 caller route 未联动。
export async function renameAdminGenModel(
  oldModelId: string,
  newModelId: string,
) {
  return request<Envelope<{ oldModelId: string; newModelId: string }>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(oldModelId)}/rename`,
    {
      method: "PATCH",
      data: { newModelId },
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function setAdminGenModelEnabled(
  modelId: string,
  enabled: boolean,
) {
  return request<Envelope<{ modelId: string; enabled: boolean }>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}/enabled`,
    {
      method: "PATCH",
      data: { enabled },
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// AdminTest=true 跳 caller route + 不扣 credit。
export interface AdminModelTestRunRequest {
  prompt?: string;
  size?: string;
  resolution?: string;
  image_urls?: string[];
  duration?: number;
  aspect_ratio?: string;
  audio?: boolean;
  ref_video_urls?: string[];
  ref_video_duration_sec?: number;
  ref_video_durations?: number[];
  voice_preset?: string;
  emotion?: string;
  speed_factor?: number;
  system?: string;
  temperature?: number;
  /** 试跑专用:额外上游参数,后端会透传到 GenvasRequest.Extra */
  extra?: Record<string, unknown>;
}

export interface AdminModelTestTaskView {
  kind: "image" | "video" | "audio" | "llm";
  modelId: string;
  taskId?: string;
  status: string;
  prompt?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  llmOutput?: string;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function adminTestRunModel(
  modelId: string,
  body: AdminModelTestRunRequest,
) {
  // 75s:LLM 后端 ctx 60s + 网络余量。
  return request<Envelope<AdminModelTestTaskView>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}/test-run`,
    {
      method: "POST",
      data: body,
      skipErrorHandler: true,
      timeout: 75_000,
    },
  );
}

export async function adminGetTestRunTask(modelId: string, taskId: string) {
  return request<Envelope<AdminModelTestTaskView>>(
    `${BASE}/api/workspace/admin/models/${encodeURIComponent(modelId)}/test-run/${encodeURIComponent(taskId)}`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// Adapter 自描述,只列实现了 Metadata() 的;Wizard 用作接入模板下拉。
export interface AdapterPresetModel {
  modelId: string;
  displayName: string;
  description?: string;
  /** @deprecated price 在 caller route 配 */
  creditPrice?: number;
  recommended?: boolean;
  paramsSchema?: Record<string, any>;
}

export interface AdapterMetadata {
  displayName: string;
  defaultName: string;
  defaultBaseURL: string;
  types: Array<"image" | "video" | "audio" | "llm">;
  description?: string;
  docsURL?: string;
  apiKeyHelpURL?: string;
  apiKeyPlaceholder?: string;
  presetModels?: AdapterPresetModel[];
}

export async function listAdminAdapters() {
  return request<Envelope<{ adapters: AdapterMetadata[] }>>(
    `${BASE}/api/workspace/admin/adapters`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export type StorageKind =
  | "import"
  | "canvas_image"
  | "canvas_video"
  | "canvas_audio"
  | "storyboard_audio";

export interface StorageKindUsage {
  kind: StorageKind;
  bytes: number;
  human: string;
  count: number;
}

export interface UserStorageUsage {
  used_bytes: number;
  used_human: string;
  file_count: number;
  quota_bytes: number;
  quota_human: string;
  // 0~100;quota_bytes=0 时为 0(不限)。
  percent: number;
  breakdown?: StorageKindUsage[];
}

export async function getUserStorage() {
  return request<Envelope<UserStorageUsage>>(
    `${BASE}/api/workspace/me/storage`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// 系统运行配置(/settings/system):RecoverStale* 卡死回收 cutoff + 退款开关
export interface RecoverPolicy {
  cutoffSec?: number;
  refundEnabled?: boolean | null;
}

export interface SystemConfig {
  defaultStaleCutoffSec: number;
  defaultRefundEnabled?: boolean | null;
  recovers?: Record<string, RecoverPolicy>;
  updatedAt?: string;
  updatedBy?: string;
  availableBusinesses: string[];
}

export async function getSystemConfig() {
  return request<Envelope<SystemConfig>>(
    `${BASE}/api/workspace/admin/system-config`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function updateSystemConfig(payload: Partial<SystemConfig>) {
  return request<Envelope<SystemConfig>>(
    `${BASE}/api/workspace/admin/system-config`,
    {
      method: "PUT",
      data: payload,
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function triggerSystemConfigRecover(business?: string) {
  // 全部业务一次性扫(15 业务 × Find+UpdateMany),后端 120s 内能完成,超时保守留 180s。
  return request<Envelope<{ recovered: Record<string, number> }>>(
    `${BASE}/api/workspace/admin/system-config/recover-now`,
    {
      method: "POST",
      params: business ? { business } : {},
      skipErrorHandler: true,
      timeout: 180_000,
    },
  );
}

// 卡死回收审计(drama_recover_events):每命中一条业务 doc 写一行,长期保留。
export interface RecoverEvent {
  id: string;
  biz: string;
  doc_id: string;
  sub_key?: string;
  user_id?: string;
  task_id?: string;
  credit_cost?: number;
  refunded: boolean;
  doc_created_at?: string;
  alive_sec?: number;
  cutoff_sec?: number;
  error: string;
  triggered_by: string;
  created_at: string;
}

export interface ListRecoverEventsParams {
  biz?: string;
  user_id?: string;
  triggered_by?: string;
  since?: string;
  until?: string;
  page?: number;
  size?: number;
}

export async function listRecoverEvents(params: ListRecoverEventsParams = {}) {
  return request<
    Envelope<{
      items: RecoverEvent[];
      total: number;
      page: number;
      size: number;
    }>
  >(`${BASE}/api/workspace/admin/recover-events`, {
    method: "GET",
    params,
    skipErrorHandler: true,
    timeout: 10_000,
  });
}

// max_concurrent / lease 已搬到 provider 级(/resources/providers Drawer「调用限流」)。
// NormalizeConcurrency 派生(0 → provider llm maxConcurrent+1 → 5)后端 Effective 已算完,直接显示。
export interface LLMEffective {
  TimeoutSeconds: number;
  StreamIdleSeconds: number;
  NormalizeConcurrency: number;
  NormalizeSceneConcurrency: number;
}

export interface LLMConfig {
  timeoutSeconds?: number | null;
  streamIdleSeconds?: number | null;
  normalizeConcurrency?: number | null;
  normalizeSceneConcurrency?: number | null;
  updatedAt?: string;
  updatedBy?: string;
  effective: LLMEffective;
}

export async function getLLMConfig() {
  return request<Envelope<LLMConfig>>(
    `${BASE}/api/workspace/admin/llm-config`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function updateLLMConfig(payload: {
  timeoutSeconds?: number | null;
  streamIdleSeconds?: number | null;
  normalizeConcurrency?: number | null;
  normalizeSceneConcurrency?: number | null;
}) {
  return request<Envelope<LLMConfig>>(
    `${BASE}/api/workspace/admin/llm-config`,
    {
      method: "PUT",
      data: payload,
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// admin 改、公开只读下发 dashboard 的全局开关。改完 30s tick 生效。

export interface ClientConfig {
  designModeEnabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export async function getClientConfig() {
  return request<Envelope<ClientConfig>>(
    `${BASE}/api/workspace/admin/client-config`,
    {
      method: "GET",
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

export async function updateClientConfig(payload: {
  designModeEnabled: boolean;
}) {
  return request<Envelope<ClientConfig>>(
    `${BASE}/api/workspace/admin/client-config`,
    {
      method: "PUT",
      data: payload,
      skipErrorHandler: true,
      timeout: 8_000,
    },
  );
}

// 三层 lookup:用户副本 → admin 全局副本 → Seed.Body(代码兜底)。本组接口只读写 admin 全局副本。

export interface PromptTemplateView {
  category: string;
  slug: string;
  name: string;
  description: string;
  is_default: boolean;
  is_modified: boolean;
  body: string;
  default_body: string;
  admin_global_body?: string;
  admin_modified: boolean;
  variables?: string[];
  updated_at?: string;
  admin_updated_at?: string;
}

export interface PromptCategoryBrief {
  category: string;
  default_slug: string;
  slug_count: number;
  admin_modified: number;
  name?: string; // 中文显示名
  description?: string; // 用途说明
  callers?: string[]; // 对应任务路由 caller key
  kind?: "system_prompt" | "prompt_segment";
}

export interface PromptRevision {
  id: string;
  body: string;
  by_user_id?: string;
  label?: string;
  created_at: string;
}

export interface PromptSaveResult {
  view: PromptTemplateView;
  missing_placeholders?: string[];
}

export async function listPromptCategories() {
  return request<Envelope<{ items: PromptCategoryBrief[] }>>(
    `${BASE}/api/workspace/admin/prompt-templates`,
    { method: "GET", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function listPromptTemplatesByCategory(category: string) {
  return request<Envelope<{ items: PromptTemplateView[] }>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}`,
    { method: "GET", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function getPromptTemplate(category: string, slug: string) {
  return request<Envelope<PromptTemplateView>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
    { method: "GET", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function savePromptTemplate(
  category: string,
  slug: string,
  body: string,
) {
  return request<Envelope<PromptSaveResult>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
    {
      method: "PUT",
      data: { body },
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function resetPromptTemplate(category: string, slug: string) {
  return request<Envelope<{ reset: boolean }>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
    { method: "DELETE", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function listPromptRevisions(
  category: string,
  slug: string,
  limit = 50,
) {
  return request<Envelope<{ items: PromptRevision[] }>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}/${encodeURIComponent(slug)}/revisions`,
    {
      method: "GET",
      params: { limit },
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function revertPromptRevision(
  category: string,
  slug: string,
  revisionId: string,
) {
  return request<Envelope<PromptTemplateView>>(
    `${BASE}/api/workspace/admin/prompt-templates/${encodeURIComponent(category)}/${encodeURIComponent(slug)}/revisions/${encodeURIComponent(revisionId)}/revert`,
    { method: "POST", skipErrorHandler: true, timeout: 10_000 },
  );
}

// Skill 库：用户覆盖 → 全局 Skill。这里只维护数据库全局 Skill。

export type AdminSkillCategory =
  | "script"
  | "manual"
  | "production"
  | "agent_core"
  | "style"
  | "genre"
  | "visual_style"
  | "custom";

export interface AdminSkillToolPolicy {
  allow?: string[];
  deny?: string[];
}

export interface AdminSkillLibraryItem {
  id: string;
  name: string;
  description: string;
  category: AdminSkillCategory;
  stages?: string[];
  body?: string;
  tools?: AdminSkillToolPolicy;
  enabled: boolean;
  builtin: boolean;
  editable: boolean;
  admin_modified?: boolean;
  compile_status?: "ready" | "compiling" | "none";
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface AdminSkillSavePayload {
  name: string;
  description?: string;
  category: AdminSkillCategory;
  stages?: string[];
  body: string;
  tools?: AdminSkillToolPolicy;
  enabled?: boolean;
}

export interface AdminSkillCreatePayload extends AdminSkillSavePayload {
  builtin_key: string;
}

export async function listAdminSkillLibrary() {
  return request<Envelope<{ items: AdminSkillLibraryItem[] }>>(
    `${BASE}/api/workspace/admin/skill-library`,
    { method: "GET", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function getAdminSkill(id: string) {
  return request<Envelope<{ item: AdminSkillLibraryItem }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}`,
    { method: "GET", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function createAdminSkill(payload: AdminSkillCreatePayload) {
  return request<Envelope<{ item: AdminSkillLibraryItem }>>(
    `${BASE}/api/workspace/admin/skill-library`,
    {
      method: "POST",
      data: payload,
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function generateAdminSkillDraft(input: {
  message: string;
  category_hint?: AdminSkillCategory;
}) {
  return request<
    Envelope<{
      draft: {
        name: string;
        description?: string;
        category?: AdminSkillCategory;
        stages?: string[];
        body?: string;
        enabled?: boolean;
      };
      questions?: string[];
    }>
  >(`${BASE}/api/workspace/agent/skill-library/generate`, {
    method: "POST",
    data: input,
    skipErrorHandler: true,
    timeout: 130_000,
  });
}

export type AdminSkillGenerateStreamEvent =
  | { kind: "thinking"; hint: string }
  | {
      kind: "draft" | "done";
      error?: string;
      draft?: {
        name: string;
        description?: string;
        category?: AdminSkillCategory;
        stages?: string[];
        body?: string;
        enabled?: boolean;
      };
      questions?: string[];
    }
  | { kind: "error"; error: string };

export function streamAdminSkillDraft(
  input: { message: string; category_hint?: AdminSkillCategory },
  onEvent: (event: AdminSkillGenerateStreamEvent) => void,
): { promise: Promise<void>; abort: () => void } {
  const controller = new AbortController();

  const promise = (async () => {
    const token = getAccessToken();
    const resp = await fetch(
      `${BASE}/api/workspace/agent/skill-library/generate/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      },
    );
    if (!resp.ok || !resp.body) {
      onEvent({ kind: "error", error: `HTTP ${resp.status}` });
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";

    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buf += decoder.decode(chunk.value, { stream: true });
      let sep = buf.indexOf("\n\n");
      while (sep >= 0) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let eventName = "message";
        const dataLines: string[] = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          else if (line.startsWith("data:"))
            dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length > 0) {
          try {
            const payload = JSON.parse(dataLines.join("\n"));
            if (eventName === "done") onEvent({ kind: "done", ...payload });
            else onEvent({ kind: payload.kind, ...payload });
          } catch {
            // 忽略异常帧,不断开用户正在看的流。
          }
        }
        sep = buf.indexOf("\n\n");
      }
    }
  })();

  return { promise, abort: () => controller.abort() };
}

export async function testAdminSkillDraft(input: {
  skill_id?: string;
  stage: string;
  sample?: string;
  draft?: {
    name: string;
    description?: string;
    category?: AdminSkillCategory;
    stages?: string[];
    body?: string;
    enabled?: boolean;
  };
  run_llm?: boolean;
  compare?: boolean;
}) {
  return request<
    Envelope<{
      stage: string;
      compiled_prompt: string;
      tool_hints?: { name: string; reason?: string }[];
      workflow_hints?: string[];
      sample_output?: string;
      baseline_output?: string;
    }>
  >(`${BASE}/api/workspace/agent/skill-library/test`, {
    method: "POST",
    data: input,
    skipErrorHandler: true,
    timeout: 130_000,
  });
}

export async function saveAdminSkill(
  id: string,
  payload: AdminSkillSavePayload,
) {
  return request<Envelope<{ item: AdminSkillLibraryItem }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      data: payload,
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export interface AdminSkillRevision {
  id: string;
  skill_id: string;
  label: string;
  snapshot: AdminSkillSavePayload;
  by_user_id?: string;
  created_at: string;
}

export async function listAdminSkillRevisions(id: string, limit = 50) {
  return request<Envelope<{ items: AdminSkillRevision[] }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}/revisions`,
    {
      method: "GET",
      params: { limit },
      skipErrorHandler: true,
      timeout: 10_000,
    },
  );
}

export async function revertAdminSkillRevision(id: string, revisionId: string) {
  return request<Envelope<{ item: AdminSkillLibraryItem }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revisionId)}/revert`,
    { method: "POST", skipErrorHandler: true, timeout: 10_000 },
  );
}

export async function resetAdminSkill(id: string) {
  return request<Envelope<{ reset: boolean }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}`,
    { method: "DELETE", skipErrorHandler: true, timeout: 10_000 },
  );
}

export interface SkillRecompileResult {
  total: number;
  queued: number;
  skipped: number;
  by_kind?: Record<string, number>;
  by_category?: Record<string, number>;
}

export async function compileAllAdminSkills() {
  return request<Envelope<SkillRecompileResult>>(
    `${BASE}/api/workspace/admin/skill-library/compile-all`,
    { method: "POST", skipErrorHandler: true, timeout: 25_000 },
  );
}

// 一键编译该全局 Skill(生成分阶段 facet)。返回最新编译状态。
export async function compileAdminSkill(id: string) {
  return request<Envelope<{ compile_status: string }>>(
    `${BASE}/api/workspace/admin/skill-library/${encodeURIComponent(id)}/compile`,
    { method: "POST", skipErrorHandler: true, timeout: 15_000 },
  );
}
